// src/components/onboarding/WelcomeModal.tsx
//
// Shown exactly once — after the OnboardingModal is complete — to first-time
// users. Contains:
//   • A warm personalised greeting with the user's first name
//   • An optional welcome video embed (YouTube iframe)
//   • A 5-slide carousel walking through the platform's key features
//   • A "Get started" button that fires PATCH /api/regions/dismiss-welcome
//     and immediately patches Redux so the modal never re-appears
//
// The modal closes only via the "Get started" button — no backdrop click,
// no keyboard escape — ensuring every new member sees the full orientation.

import React, { useState } from "react";
import {
  BookOpen, ClipboardList, Star, TrendingUp, Users,
  ChevronLeft, ChevronRight, X, Loader2, PlayCircle,
} from "lucide-react";
import { useDismissWelcomeMutation } from "../app/api/regionApi";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { patchOnboarding } from "../app/features/authSlice";

/* ─────────────────────────────────────────────────────────────────
   SLIDE DATA
───────────────────────────────────────────────────────────────── */

const SLIDES = [
  {
    icon:     BookOpen,
    colour:   "bg-green-100 text-green-600",
    title:    "Your content library",
    body:     "Each quarter you'll have access to a curated library of keynote sessions, panel discussions, reading lists, and resources — all designed to deepen your understanding of kingdom leadership. Browse, watch, and track your progress from the Library tab.",
  },
  {
    icon:     ClipboardList,
    colour:   "bg-amber-100 text-amber-600",
    title:    "Quarterly tasks",
    body:     "Every quarter comes with a set of leadership tasks — practical exercises that move you from passive learning to active application. Submit your responses, save drafts as you go, and track your completion percentage from the Tasks tab.",
  },
  {
    icon:     TrendingUp,
    colour:   "bg-blue-100 text-blue-600",
    title:    "Your journey & progress",
    body:     "Your Journey page gives you a full picture of your programme history — quarters completed, tasks submitted, content engaged, and your overall progression through the programme. Every action you take is recorded.",
  },
  {
    icon:     Star,
    colour:   "bg-purple-100 text-purple-600",
    title:    "Legacy Fellows",
    body:     "The Legacy Fellows programme recognises the top 10 members each quarter — those who engage deeply, complete all tasks, and demonstrate a commitment to growth. If you're nominated by your regional representative, you'll see a notification on your dashboard.",
  },
  {
    icon:     Users,
    colour:   "bg-rose-100 text-rose-600",
    title:    "Community & connection",
    body:     "You're not doing this alone. The Community tab connects you with announcements, discussion threads, and regional updates from your regional coordinator. Engage with peers, ask questions, and grow together.",
  },
];

/* ─────────────────────────────────────────────────────────────────
   VIDEO SECTION
   Replace WELCOME_VIDEO_ID with your actual YouTube video ID.
   If you don't have a video yet, set SHOW_VIDEO = false.
───────────────────────────────────────────────────────────────── */

const WELCOME_VIDEO_ID = "";    // e.g. "dQw4w9WgXcQ"
const SHOW_VIDEO       = Boolean(WELCOME_VIDEO_ID);

/* ─────────────────────────────────────────────────────────────────
   SLIDE COMPONENT
───────────────────────────────────────────────────────────────── */

const Slide: React.FC<{ slide: typeof SLIDES[number]; index: number; total: number }> = ({ slide, index, total }) => (
  <div className="flex flex-col items-center text-center px-2">
    <div className={`w-14 h-14 rounded-2xl ${slide.colour} flex items-center justify-center mb-5`}>
      <slide.icon size={26} />
    </div>
    <p className="text-[10px] font-black uppercase tracking-[0.18em] text-gray-400 mb-2">
      {index + 1} of {total}
    </p>
    <h3 className="text-xl font-black text-gray-900 mb-3 leading-snug">
      {slide.title}
    </h3>
    <p className="text-sm text-gray-500 leading-relaxed max-w-sm">
      {slide.body}
    </p>
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────────────── */

const WelcomeModal: React.FC = () => {
  const dispatch   = useAppDispatch();
  const user       = useAppSelector((s) => s.auth.user);
  const firstName  = user?.firstName ?? "there";

  const [slideIndex,   setSlideIndex]   = useState(0);
  const [showVideo,    setShowVideo]    = useState(false);
  const [dismissed,    setDismissed]    = useState(false);

  const [dismissWelcome, { isLoading: dismissing }] = useDismissWelcomeMutation();

  const isLast = slideIndex === SLIDES.length - 1;

  const prev = () => setSlideIndex((i) => Math.max(0, i - 1));
  const next = () => setSlideIndex((i) => Math.min(SLIDES.length - 1, i + 1));

  const handleGetStarted = async () => {
    try {
      await dismissWelcome().unwrap();
    } catch {
      // Fire and forget — even if the request fails we still close the modal
      // so we don't trap the user. The next login will retry via the auth response.
    } finally {
      // Patch Redux immediately — modal disappears at once
      dispatch(patchOnboarding({ hasSeenWelcome: true }));
      setDismissed(true);
    }
  };

  if (dismissed) return null;

  return (
    <div className="fixed inset-0 z-[9998] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">

        {/* ── Hero / greeting ── */}
        <div className="bg-gradient-to-br from-green-700 to-green-950 px-7 pt-8 pb-7 text-white shrink-0">
          {/* Scripture badge */}
          <div className="inline-flex items-center gap-2 border border-green-500/50 rounded-full px-3 py-1 text-green-300 text-[10px] font-black tracking-[0.18em] uppercase mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            Proverbs 4:7 — Get wisdom, get understanding
          </div>

          <h1 className="text-2xl sm:text-3xl font-black leading-tight mb-2">
            Welcome, {firstName}! 🎉
          </h1>
          <p className="text-green-200 text-sm leading-relaxed">
            You've joined the GIMLP Mentorship Programme. This brief orientation
            will walk you through how to get the most from your journey.
          </p>

          {/* Video CTA — only rendered when a video ID is set */}
          {SHOW_VIDEO && !showVideo && (
            <button
              onClick={() => setShowVideo(true)}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 bg-white/15 hover:bg-white/25 rounded-xl text-sm font-bold transition-all"
            >
              <PlayCircle size={16} className="text-green-300" />
              Watch the welcome video
            </button>
          )}
        </div>

        {/* ── Video player (conditionally shown) ── */}
        {SHOW_VIDEO && showVideo && (
          <div className="relative bg-black shrink-0">
            <iframe
              src={`https://www.youtube.com/embed/${WELCOME_VIDEO_ID}?autoplay=1&rel=0`}
              title="Welcome to GIMLP"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full aspect-video"
            />
            <button
              onClick={() => setShowVideo(false)}
              className="absolute top-2 right-2 p-1.5 bg-black/60 hover:bg-black/80 rounded-lg text-white transition-colors"
            >
              <X size={14} />
            </button>
          </div>
        )}

        {/* ── Carousel ── */}
        <div className="flex-1 overflow-y-auto px-7 py-7">
          <Slide
            slide={SLIDES[slideIndex]}
            index={slideIndex}
            total={SLIDES.length}
          />
        </div>

        {/* ── Dot indicator ── */}
        <div className="flex items-center justify-center gap-1.5 pb-2 shrink-0">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => setSlideIndex(i)}
              className={`rounded-full transition-all ${
                i === slideIndex
                  ? "w-5 h-1.5 bg-green-600"
                  : "w-1.5 h-1.5 bg-gray-200 hover:bg-gray-300"
              }`}
            />
          ))}
        </div>

        {/* ── Navigation footer ── */}
        <div className="px-7 pb-7 pt-3 flex items-center justify-between gap-4 shrink-0 border-t border-gray-100">
          <button
            onClick={prev}
            disabled={slideIndex === 0}
            className="inline-flex items-center gap-1 text-xs font-semibold text-gray-400 hover:text-gray-600 disabled:opacity-30 transition-colors"
          >
            <ChevronLeft size={14} /> Previous
          </button>

          {isLast ? (
            <button
              onClick={handleGetStarted}
              disabled={dismissing}
              className="inline-flex items-center gap-2 rounded-xl bg-green-600 hover:bg-green-500 disabled:opacity-70 text-white font-black uppercase tracking-widest text-xs px-6 py-3 transition-all active:scale-95"
            >
              {dismissing
                ? <><Loader2 size={13} className="animate-spin" /> Saving…</>
                : <>Get started <ChevronRight size={13} /></>}
            </button>
          ) : (
            <button
              onClick={next}
              className="inline-flex items-center gap-1 text-xs font-black text-green-600 hover:text-green-700 transition-colors uppercase tracking-widest"
            >
              Next <ChevronRight size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WelcomeModal;